// src/pages/VentingRoomContent/UsageReportContent.js
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { BsCameraVideo, BsImage, BsFileEarmarkText, BsMusicNote } from 'react-icons/bs';
import { AiOutlinePlus } from 'react-icons/ai';
import '../../styles/VentingRoomDashboard.css';
import { UserContext } from '../../UserContext';

const API_BASE = "http://localhost:5000";

function UsageReportContent() {
  const { userEmail, isAuthenticated } = useContext(UserContext);
  const navigate = useNavigate();
  const [contentType, setContentType] = useState('Videos');
  const [postedVideos, setPostedVideos] = useState([]);
  const [postedImages, setPostedImages] = useState([]);
  const [postedDocs, setPostedDocs] = useState([]);
  const [postedAudio, setPostedAudio] = useState([]);

  // Engagement data states
  const [postsPerDayData, setPostsPerDayData] = useState({ you: 0, min: 0, average: 0, max: 0 });
  const [likesStatsData, setLikesStatsData] = useState({ yourLikes: 0, totalLikes: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const calculateEngagementMetrics = useCallback((posts, userId) => {
    const postsByDayByUser = {};
    let currentUserDailyCounts = {};
    let totalLikesCount = 0;
    let yourLikesCount = 0;

    posts.forEach(post => {
      const postDate = new Date(post.timestamp).toISOString().split('T')[0];

      postsByDayByUser[post.userId] = postsByDayByUser[post.userId] || {};
      postsByDayByUser[post.userId][postDate] = (postsByDayByUser[post.userId][postDate] || 0) + 1;

      const postLikes = post.likes || [];
      totalLikesCount += postLikes.length;
      if (postLikes.includes(userId)) {
        yourLikesCount++;
      }

      if (post.userId === userId) {
        currentUserDailyCounts[postDate] = (currentUserDailyCounts[postDate] || 0) + 1;
      }
    });

    let allDailyCounts = [];
    let maxOverallDailyPosts = 0;

    for (const user in postsByDayByUser) {
      const userDailyCounts = Object.values(postsByDayByUser[user]);
      allDailyCounts = allDailyCounts.concat(userDailyCounts);
      if (userDailyCounts.length > 0) {
        const userMaxDaily = Math.max(...userDailyCounts);
        if (userMaxDaily > maxOverallDailyPosts) {
          maxOverallDailyPosts = userMaxDaily;
        }
      }
    }

    const youPostsPerDay = Object.values(currentUserDailyCounts).length > 0
      ? Math.round(Object.values(currentUserDailyCounts).reduce((sum, count) => sum + count, 0) / Object.values(currentUserDailyCounts).length)
      : 0;

    const minOverallPostsPerDay = allDailyCounts.length > 0 ? Math.min(...allDailyCounts) : 0;
    const averageOverallPostsPerDay = allDailyCounts.length > 0 ? Math.round(allDailyCounts.reduce((sum, count) => sum + count, 0) / allDailyCounts.length) : 0;

    setPostedVideos(posts.filter(p => p.type === 'video'));
    setPostedImages(posts.filter(p => p.type === 'image'));
    setPostedDocs(posts.filter(p => p.type === 'doc'));
    setPostedAudio(posts.filter(p => p.type === 'audio'));

    setPostsPerDayData({
      you: youPostsPerDay,
      min: minOverallPostsPerDay,
      average: averageOverallPostsPerDay,
      max: maxOverallDailyPosts,
    });

    setLikesStatsData({
      yourLikes: yourLikesCount,
      totalLikes: totalLikesCount,
    });
  }, []);

  // Fetch posts and calculate metrics
  useEffect(() => {
    if (!isAuthenticated || !userEmail) {
      setLoading(false);
      setError("User not authenticated.");
      return;
    }

    setLoading(true);
    setError(null);

    const fetchAllPosts = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/venting-room/all-posts`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        calculateEngagementMetrics(data.posts, userEmail);
      } catch (err) {
        console.error("Error fetching posts:", err);
        setError("Failed to fetch posts data.");
      } finally {
        setLoading(false);
      }
    };

    fetchAllPosts();
  }, [isAuthenticated, userEmail, calculateEngagementMetrics]);

  const handleContentTypeChange = (event) => {
    setContentType(event.target.value);
  };

  const handleAddPostClick = () => {
    navigate('/dev-venting-room-dashboard/dashboard');
  };

  if (loading) {
    return (
      <div className="usage-report-container">
        <p>Loading engagement reports...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="usage-report-container">
        <p className="error-message">Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="usage-report-container">
      <div className="report-header">
        <h2>USAGE REPORT</h2>
      </div>

      <div className="user-id-display">
        <p>Current User: {userEmail}</p>
        <p>This ID is important for multi-user interactions.</p>
      </div>

      <div className="content-posted-section">
        <h3>CONTENT POSTED</h3>
        <div className="content-posted-header">
          <h4>Content</h4>
          <div className="content-filter">
            <select value={contentType} onChange={handleContentTypeChange}>
              <option>Images</option>
              <option>Videos</option>
              <option>Docs</option>
              <option>Audio</option>
            </select>
          </div>
          <div className="content-grid">
            {contentType === 'Videos' ? (
              postedVideos.length > 0 ? (
                postedVideos.map((video) => (
                  <div key={video.id} className="content-item video-item">
                    <BsCameraVideo className="content-icon" />
                    <span>{video.fileName}</span>
                  </div>
                ))
              ) : (
                <div className="content-item add-post-card add-video-card" onClick={handleAddPostClick}>
                  <div className="add-icon-wrapper">
                    <BsCameraVideo className="add-content-icon" />
                    <AiOutlinePlus className="add-plus-icon" />
                  </div>
                  <span>Add Video Post</span>
                </div>
              )
            ) : contentType === 'Images' ? (
              postedImages.length > 0 ? (
                postedImages.map((image) => (
                  <div key={image.id} className="content-item image-item">
                    <BsImage className="content-icon" />
                    <span>{image.fileName}</span>
                  </div>
                ))
              ) : (
                <div className="content-item add-post-card add-image-card" onClick={handleAddPostClick}>
                  <div className="add-icon-wrapper">
                    <BsImage className="add-content-icon" />
                    <AiOutlinePlus className="add-plus-icon" />
                  </div>
                  <span>Add Image Post</span>
                </div>
              )
            ) : contentType === 'Docs' ? (
              postedDocs.length > 0 ? (
                postedDocs.map((doc) => (
                  <div key={doc.id} className="content-item doc-item">
                    <BsFileEarmarkText className="content-icon" />
                    <span>{doc.fileName}</span>
                  </div>
                ))
              ) : (
                <div className="content-item add-post-card add-doc-card" onClick={handleAddPostClick}>
                  <div className="add-icon-wrapper">
                    <BsFileEarmarkText className="add-content-icon" />
                    <AiOutlinePlus className="add-plus-icon" />
                  </div>
                  <span>Add Document Post</span>
                </div>
              )
            ) : contentType === 'Audio' ? (
              postedAudio.length > 0 ? (
                postedAudio.map((audio) => (
                  <div key={audio.id} className="content-item audio-item">
                    <BsMusicNote className="content-icon" />
                    <span>{audio.fileName}</span>
                  </div>
                ))
              ) : (
                <div className="content-item add-post-card add-audio-card" onClick={handleAddPostClick}>
                  <div className="add-icon-wrapper">
                    <BsMusicNote className="add-content-icon" />
                    <AiOutlinePlus className="add-plus-icon" />
                  </div>
                  <span>Add Audio Post</span>
                </div>
              )
            ) : (
              <p>No content available for this type.</p>
            )}
          </div>
        </div>
      </div>

      {/* Engagement Charts - Restored */}
      <div className="engagement-charts-section">
        <h3>ENGAGEMENT CHARTS</h3>
        <div className="engagement-charts">
          <div className="chart-container">
            <h4>Posts Per Day</h4>
            <div className="bar-chart">
              <div className="bar you" style={{ height: `${(postsPerDayData.you / postsPerDayData.max) * 100}%` }}>
                <span>You ({postsPerDayData.you})</span>
              </div>
              <div className="bar min" style={{ height: `${(postsPerDayData.min / postsPerDayData.max) * 100}%` }}>
                <span>Min ({postsPerDayData.min})</span>
              </div>
              <div className="bar average" style={{ height: `${(postsPerDayData.average / postsPerDayData.max) * 100}%` }}>
                <span>Avg ({postsPerDayData.average})</span>
              </div>
              <div className="bar max" style={{ height: `${(postsPerDayData.max / postsPerDayData.max) * 100}%` }}>
                <span>Max ({postsPerDayData.max})</span>
              </div>
            </div>
          </div>

          <div className="chart-container">
            <h4>Likes Stats</h4>
            <div className="pie-chart-container">
              <div className="pie-chart" style={{ backgroundImage: `conic-gradient(#f44336 ${likesStatsData.yourLikes}%, #e0e0e0 0)` }}></div>
              <div className="pie-chart-legend">
                <div className="legend-item">
                  <span className="legend-color your-likes"></span> Your likes ({likesStatsData.yourLikes})
                </div>
                <div className="legend-item">
                  <span className="legend-color total-likes"></span> Total likes ({likesStatsData.totalLikes})
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UsageReportContent;
